import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Users, Calendar, DollarSign, TrendingUp, Image as ImageIcon, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const AdminPanel = () => {
  const recentBookings = [
    { id: 1, photographer: 'Sarah Johnson', client: 'John Smith', amount: 4500, date: 'Dec 15, 2024', status: 'completed' },
    { id: 2, photographer: 'Michael Chen', client: 'TechCorp', amount: 2500, date: 'Jan 20, 2025', status: 'confirmed' },
    { id: 3, photographer: 'Emma Rodriguez', client: 'Fashion Co', amount: 3000, date: 'Jan 25, 2025', status: 'pending' },
  ];

  const recentUsers = [
    { id: 1, name: 'David Kim', type: 'Photographer', joined: '2 days ago', status: 'active' },
    { id: 2, name: 'Lisa Anderson', type: 'Client', joined: '3 days ago', status: 'active' },
    { id: 3, name: 'Alex Turner', type: 'Photographer', joined: '5 days ago', status: 'pending' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest Admin</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" data-testid="admin-badge">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
              <Button variant="ghost" size="icon" data-testid="profile-button">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
                  alt="Admin"
                  className="w-8 h-8 rounded-full object-cover"
                />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">
            Manage users, bookings, and platform analytics
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Total Revenue
                <DollarSign className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold mb-1" data-testid="stat-revenue">$248,560</div>
              <div className="text-xs text-green-600 flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" />
                +18% from last month
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Total Users
                <Users className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-users">1,247</div>
              <div className="text-xs text-muted-foreground">487 photographers</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Total Bookings
                <Calendar className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-bookings">892</div>
              <div className="text-xs text-muted-foreground">128 this month</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Assets Stored
                <ImageIcon className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-assets">124K</div>
              <div className="text-xs text-muted-foreground">2.4 TB storage</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Bookings */}
          <Card data-testid="recent-bookings">
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
              <CardDescription>Latest transactions on the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`booking-${booking.id}`}>
                    <div className="flex-1">
                      <div className="font-medium text-sm" data-testid={`booking-client-${booking.id}`}>{booking.client}</div>
                      <div className="text-xs text-muted-foreground" data-testid={`booking-photographer-${booking.id}`}>
                        with {booking.photographer}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-testid={`booking-date-${booking.id}`}>{booking.date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold" data-testid={`booking-amount-${booking.id}`}>${booking.amount.toLocaleString()}</div>
                      <Badge
                        variant={booking.status === 'completed' ? 'default' : booking.status === 'confirmed' ? 'secondary' : 'outline'}
                        className="text-xs"
                        data-testid={`booking-status-${booking.id}`}
                      >
                        {booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4" data-testid="view-all-bookings">
                View All Bookings
              </Button>
            </CardContent>
          </Card>

          {/* Recent Users */}
          <Card data-testid="recent-users">
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
              <CardDescription>New registrations and signups</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`user-${user.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-medium text-sm" data-testid={`user-name-${user.id}`}>{user.name}</div>
                        <div className="text-xs text-muted-foreground" data-testid={`user-type-${user.id}`}>{user.type}</div>
                        <div className="text-xs text-muted-foreground" data-testid={`user-joined-${user.id}`}>{user.joined}</div>
                      </div>
                    </div>
                    <Badge
                      variant={user.status === 'active' ? 'default' : 'secondary'}
                      data-testid={`user-status-${user.id}`}
                    >
                      {user.status}
                    </Badge>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4" data-testid="view-all-users">
                View All Users
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mt-8" data-testid="quick-actions">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Button variant="outline" className="justify-start" data-testid="action-users">
                <Users className="w-4 h-4 mr-2" />
                Manage Users
              </Button>
              <Button variant="outline" className="justify-start" data-testid="action-bookings">
                <Calendar className="w-4 h-4 mr-2" />
                View Bookings
              </Button>
              <Button variant="outline" className="justify-start" data-testid="action-analytics">
                <TrendingUp className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <Button variant="outline" className="justify-start" data-testid="action-settings">
                <Shield className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;